import { useQueryParams, useHistory } from '@fs/zion-router'
import React, { useMemo } from 'react'
import { SortableResponsiveTable, ResponsiveTableRow, useSortableTable, MarginBox } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePlaceReportsPageData } from '../../PlaceReportsProvider'
import { getLocaleName, propertySort } from '../../placeReportsHelper'
import ContentWrapper from '../../ContentWrapper'
import { type Division } from '../../types'

const columnProperties: Record<string, keyof Division> = {
  column1: 'id',
  column2: 'name',
  column3: 'score',
}

export default function PlaceReportsDivisionsNavPanel(): JSX.Element {
  const [t] = useTranslation()
  const { getCountryAndRegion } = usePlaceReportsPageData()
  const { query } = useQueryParams()
  const { region, country, division } = query
  const history = useHistory()
  const sortable = useSortableTable('column1', null)
  const { sortedColumnId, ascending } = sortable
  const sortedData = useMemo(() => {
    const countryObj = getCountryAndRegion(country)?.country
    const divisions = countryObj?.divisions || []
    return {
      divisions: divisions?.toSorted(propertySort(columnProperties[sortedColumnId], ascending)),
      country: countryObj,
    }
  }, [ascending, sortedColumnId, getCountryAndRegion, country])

  return (
    <ContentWrapper>
      <MarginBox marginX="nano" marginY="sm">
        <SortableResponsiveTable
          {...sortable}
          atSizeColumns={{
            default: {
              labels: [
                t('place-reports.nav-panel.division.label', 'Division'),
                t('place-reports.nav-panel.score.label', 'Score'),
              ],
            },
          }}
          sortableColumns={['column1', 'column2']}
          stickyHeader
          totalColumnCount={2}
          separator={<br />}
        >
          {sortedData?.divisions.map((item) => {
            const { id, name, score } = item
            return (
              <ResponsiveTableRow
                selected={id === division}
                key={id}
                column1={getLocaleName(name, sortedData?.country?.native_lang)}
                column2={score}
                onClick={() => {
                  history.push(`/place-reports/?region=${region}&country=${country}&division=${id}`)
                }}
                tabIndex={0}
              />
            )
          })}
        </SortableResponsiveTable>
      </MarginBox>
    </ContentWrapper>
  )
}
